import { useState, useEffect } from "react";
import { createModel } from "hox";
import useGlobalModel, { transPgToPagination } from "@/model/globalModel";
import { ISearchPage } from "@/types/AppTypes";
import { convertRes2Blob } from "@/utils/exportFile";
import { warehouseService, IPositionFormFields, IpositionInsert, IpositionSearch, IpositionDetail, IpositionSearchFields, IpositionBatchInsert, IPositionUpdate, IWarehouseDetail, IWarehouseUpdate } from "../../warehouseService";
import { globalPrompt } from "@/components/message";
import { TablePaginationConfig } from "antd/lib/table";
import { DEFAULT_PAGE_SIZE, DEFAULT_FIRST_PAGE_NUM, defaultMdCmParam } from "@/utils/constants";


export interface IPositionDrawerInfo {
    visible: boolean,
    mode: "add" | "edit"
}


export const useWarehouseDetailModel = createModel(function () {
    const { user: { shopCode,shopId, shopName, userName }, setLoadingInfo, warehouseloadDataAll } = useGlobalModel(({ user }) => [user]);
    const initAddFormVal = {
        id: "",
        code: "",
        name: "",
        shopName,
        createUser: userName,
        status: true
    }
    //编辑表单缓存
    const [editFormVal, setEditFormVal] = useState<IWarehouseDetail>(initAddFormVal);
    //表单新增修改
    const updateEditFormVal = (val: object | null) => {
        val ? setEditFormVal({ ...editFormVal, ...val }) : setEditFormVal(initAddFormVal);
    }

    //获取一条详情数据 Api
    const getWarehouseOne = async (id: string) => {
        let { retData } = await warehouseService.one(id);
        await setEditFormVal(retData);
        await positionLoadData(
            { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM },
            { warehouseId: retData.id, warehouseCode: retData.code }
        )
    }

    //表单编辑 Api
    const editWarehouse = async (val: IWarehouseDetail) => {
        let data: IWarehouseUpdate = {
            ...val,
            shopCode,
            shopName,
            orgCode: shopId,
            orgName: shopName,
            sourceApp: defaultMdCmParam.sourceApp, branch: defaultMdCmParam.branch,
        }
        let { retData } = await warehouseService.update(data);
        globalPrompt("message", { text: "仓库编辑成功", type: "success" })
        warehouseloadDataAll();
        return retData;
    }


    /**
    * 仓库 货位
    */
    const initDrawerInfo: IPositionDrawerInfo = { visible: false, mode: "add" };
    const [positionDrawer, setpositionDrawer] = useState<IPositionDrawerInfo>(initDrawerInfo);
    const setIpositionDrawer = (str?: "add" | "edit") => setpositionDrawer(str ? { visible: true, mode: str } : initDrawerInfo);
    const [positionSearch, setPositionSearch] = useState<IpositionSearchFields>({});
    const [selectedPositionRows, setSelectedPositionRows] = useState<IpositionDetail[]>([]); //多选Position行信息
    const [positionSource, setPositionSource] = useState<IpositionDetail[]>([]); //区域数据源
    const [positionPgCfg, setPositionPgCfg] = useState<TablePaginationConfig>({ pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM });  //分页参数

    //货位列表 Api
    const positionLoadData = async (page?: ISearchPage, searchValue: IpositionSearch = positionSearch) => {
        const searchParams = {
            ...searchValue,
            shopId,
            warehouseId: searchValue.warehouseId ?? editFormVal.id,
            warehouseCode: searchValue.warehouseCode ?? editFormVal.code,
            pageSize: page?.pageSize ?? positionPgCfg.pageSize ?? DEFAULT_PAGE_SIZE,
            pageNum: page?.pageNum ?? positionPgCfg.current ?? DEFAULT_FIRST_PAGE_NUM,
        }
        let { retData } = await warehouseService.positionPage(searchParams);
        const { records, ...pg } = retData;
        setPositionPgCfg(transPgToPagination(pg));
        setPositionSource(records);
    }

    //货位新增 Api
    const insertPosition = async (fields: IPositionFormFields) => {
        let { isBatch, num, prefix, startCode, ...val } = fields;
        let data: IpositionInsert = {
            ...val,
            shopId, shopName,
            warehouseId: editFormVal.id,
            warehouseCode: editFormVal.code
        }
        let { retData } = await warehouseService.positionInsert(data);
        positionLoadData();
        globalPrompt("message", { text: "货位新增成功", type: "success" });
        setIpositionDrawer();
    }
    //货位批量新增 Api
    const batchInserttPosition = async (fields: IPositionFormFields) => {
        let { isBatch, ...val } = fields;
        let data: IpositionBatchInsert = {
            ...val,
            shopId, shopName,
            warehouseId: editFormVal.id,
            warehouseCode: editFormVal.code,
            code: ""
        }

        let { retData } = await warehouseService.positionBatchInsert(data);
        positionLoadData();
        globalPrompt("message", { text: "货位新增成功", type: "success" });
        setIpositionDrawer();
    }

    //货位编辑 Api
    const updatePosition = async (fields: IPositionFormFields) => {
        let { isBatch, ...val } = fields;
        let data: IPositionUpdate = {
            ...val,
            id: selectedPositionRows[0]?.id,
            shopId, shopName,
            warehouseId: editFormVal.id,
            warehouseCode: editFormVal.code
        }
        let { retData } = await warehouseService.positionUpdate(data);
        positionLoadData();
        globalPrompt("message", { text: "货位编辑成功", type: "success" });
        setIpositionDrawer();
    }

    //货架下载模板
    const positionDownload = async () => {
        let result = await warehouseService.positionDownloadTpl();
        convertRes2Blob(result);
    }

    //货架下载模板
    const positionExport = async () => {
        setLoadingInfo({ loadingVisible: true, txt: "快速导出中" });
        try {
            let result = await warehouseService.positionExport(editFormVal.id);
            result && setLoadingInfo({ loadingVisible: false, txt: "" });
            convertRes2Blob(result);
        } catch { setLoadingInfo({ loadingVisible: false, txt: "" }) }
    }


    return {
        /***field***/
        editFormVal, setEditFormVal,
        getWarehouseOne,
        updateEditFormVal,
        editWarehouse,
        //货位
        positionSource,
        positionDrawer, setIpositionDrawer,
        selectedPositionRows, setSelectedPositionRows,
        positionPgCfg, setPositionPgCfg,
        insertPosition,
        positionExport,
        positionDownload,
        updatePosition,
        batchInserttPosition,
        positionLoadData
    }
});